import moment from 'moment';
import {
  ResourceAllocations,
  ResourcesToBeFetched,
} from 'api/resource-management/types';
import {
  getInitialResourcesToBeFetched,
  getTotalAllocatedMinutes,
} from 'components/organisms/resource-management/utils/common';
import { getCorrectedDateRangeBasedOnView } from 'components/organisms/resource-management/utils/dates';
import { convertAllocationsByUsersOrTeamsToEvents } from 'components/organisms/resource-management/utils/events-conversion/allocations-by-users-or-teams';
import { isOverlapping } from 'components/organisms/resource-management/utils/events-conversion/common';
import { getResourceSummaries } from 'components/organisms/resource-management/utils/resources-summary/resource-summary';
import {
  ResourceManagementEvents,
  SchedulerEventType,
} from '../../event-types';
import { ViewPresetId } from '../../scheduler-config/view-presets/constants';
import { GroupBy, Range } from '../../types';
import { useAllocationStateManager } from '../allocation-state-manager';
import { CommonManagerProps } from '../types';

interface ReturnProps {
  updateAllocationAndSummary: (
    resourcesToBeFetched: Partial<ResourcesToBeFetched<[number]>>,
    range: Range,
  ) => Promise<ResourceAllocations>;
}

export const useAllocationAndSummaryManager = (
  props: CommonManagerProps,
): ReturnProps => {
  const {
    groupBy,
    schedulerRef,
    currentViewPreset,
    resourceManagementConfig,
    workingDaysConfig,
    userLookup,
    teamMembership,
  } = props;

  const { loadAllocations } = useAllocationStateManager(props);

  const updateAllocationAndSummary = async (
    resourceToBeFetched: Partial<ResourcesToBeFetched<[number]>>,
    range: Range,
  ) => {
    const resources: ResourcesToBeFetched<[number]> = {
      ...getInitialResourcesToBeFetched(),
      ...resourceToBeFetched,
    };

    return loadAllocations({ resources, range }).then((allocations) => {
      const events: ResourceManagementEvents[] = [];
      const newAllocationEvents: ResourceManagementEvents[] =
        convertAllocationsByUsersOrTeamsToEvents(
          allocations,
          {
            excludeNonWorkingDays: true,
            workingDaysConfig,
          },
          resourceManagementConfig?.softAllocations.enabled,
        );

      const allEvents = [...newAllocationEvents];

      if (groupBy === GroupBy.Users) {
        const { fromDate: correctedFromDate, toDate: correctedToDate } =
          getCorrectedDateRangeBasedOnView(
            {
              fromDate: moment(schedulerRef.current.instance.startDate),
              toDate: moment(schedulerRef.current.instance.endDate),
            },
            currentViewPreset,
            workingDaysConfig,
          );

        const isUpdatedSummaryInVisibleRange = isOverlapping(
          {
            startDate: correctedFromDate,
            endDate: correctedToDate,
          },
          {
            startDate: range.fromDate,
            endDate: range.toDate,
          },
        );

        if (
          ![ViewPresetId.Day, ViewPresetId.DayZoomed].includes(
            currentViewPreset,
          ) ||
          isUpdatedSummaryInVisibleRange
        ) {
          const { userSummaries, teamSummaries, companySummaries } =
            getResourceSummaries(
              userLookup,
              teamMembership,
              allocations,
              currentViewPreset,
              resourceManagementConfig?.softAllocations.enabled,
            );

          allEvents.push(
            ...Object.values(userSummaries).flat(),
            ...Object.values(teamSummaries).flat(),
            ...Object.values(companySummaries).flat(),
          );
        }
      }

      schedulerRef.current.instance.suspendRefresh();

      allEvents.forEach((event) => {
        if (
          event.type === SchedulerEventType.Allocation ||
          ![ViewPresetId.Day, ViewPresetId.DayZoomed].includes(
            currentViewPreset,
          )
        ) {
          const existingEvent =
            schedulerRef.current.instance.eventStore.getById(event.id);

          if (existingEvent) {
            schedulerRef.current.instance.eventStore.remove(existingEvent);
          }
        }

        if (
          event.type === SchedulerEventType.TeamAllocationSummary ||
          event.type === SchedulerEventType.CompanyAllocationSummary
        ) {
          const minutes = getTotalAllocatedMinutes(event.allocatedMinutes);

          if (minutes === 0) {
            return;
          }
        }
        events.push(event);
      });

      schedulerRef.current.instance.eventStore.add(events);
      schedulerRef.current.instance.resumeRefresh(true);

      return allocations;
    });
  };

  return {
    updateAllocationAndSummary,
  };
};
